import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { claimPointer, pointerSamples, DEFAULT_INK, STROKE_WIDTH } from '../../store/illustrations.js';

// Drawing over the brief and the goal sheet (Jimmy, 2026-10-19): circling a
// figure, underlining a line, as the advisor could on the slides.
//
// A slide is one fixed page, so its ink could be measured against the page.
// The brief is a document that flows onto as many pages as it needs, and where
// a page breaks depends on the paper, so a mark is tied to the block it was
// drawn over instead: a row of the goal sheet, a topic, a paragraph, a table
// row. Each such block carries `data-ink="<key>"`, and a mark is kept as
// points in CSS pixels from that block's top left corner. The block lays out
// at the same width in the meeting (scaled to fit the column), on the
// takeaway screen and on paper, so the mark lands on the same words wherever
// the page breaks. A mark made in a margin belongs to the nearest block.
//
// The keys are the block's identity in words, not its position, so a mark
// stays on its topic when the brief is edited around it, and a mark whose
// block is gone (a topic left out) simply no longer shows.

/** The step the brief's drawing is kept under, beside the slides' moments. */
export const ANCHORED_INK_STEP = 'brief';

// Points closer than this, in CSS pixels, add nothing anyone can see.
const MIN_STEP = 0.75;
const round = n => Math.round(n * 10) / 10;

// The chrome keeps working with the marker in hand.
const CHROME = 'button, input, select, textarea, a, summary, .conv-draw-widget, .brf-takeaway-wait, .conv-save-alert';

const InkContext = createContext(null);

/**
 * The marks for the blocks inside it. `strokes` are the saved ones, each
 * `{ a, c, w, p }` with `a` the block's key; `live` is the one being drawn.
 */
export function AnchoredInkProvider({ strokes = [], live = null, children }) {
  const value = useMemo(() => {
    const byAnchor = new Map();
    for (const s of strokes) {
      if (!s || typeof s.a !== 'string' || !Array.isArray(s.p)) continue;
      if (!byAnchor.has(s.a)) byAnchor.set(s.a, []);
      byAnchor.get(s.a).push(s);
    }
    return { byAnchor, live };
  }, [strokes, live]);
  return <InkContext.Provider value={value}>{children}</InkContext.Provider>;
}

/** The attribute that makes an element a block a mark can belong to. */
export const inkAt = key => ({ 'data-ink': key });

function points(p) {
  return p.filter(pt => Array.isArray(pt) && Number.isFinite(pt[0]) && Number.isFinite(pt[1]));
}

/**
 * The marks that belong to one block, drawn from its top left corner. Put it
 * inside the element that carries `inkAt(key)`. Outside a provider, as on the
 * brief's own editing screen, it draws nothing.
 */
export function Ink({ at }) {
  const ctx = useContext(InkContext);
  if (!ctx) return null;
  const saved = ctx.byAnchor.get(at) || [];
  const list = ctx.live && ctx.live.a === at ? [...saved, ctx.live] : saved;
  if (!list.length) return null;
  return (
    <svg className="conv-anchored-ink" aria-hidden="true" width="1" height="1">
      {list.map((s, i) => {
        const p = points(s.p);
        const w = s.w || STROKE_WIDTH;
        if (p.length === 1) {
          // eslint-disable-next-line react/no-array-index-key
          return <circle key={i} cx={p[0][0]} cy={p[0][1]} r={w / 2} fill={s.c || DEFAULT_INK} />;
        }
        return (
          <polyline
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            points={p.map(([x, y]) => `${x},${y}`).join(' ')}
            fill="none"
            stroke={s.c || DEFAULT_INK}
            strokeWidth={w}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
    </svg>
  );
}

/** The block under a point, or else the nearest one inside `root`. */
function anchorFor(root, target, x, y) {
  const hit = target?.closest?.('[data-ink]');
  if (hit && root.contains(hit)) return hit;
  let best = null;
  let bestD = Infinity;
  for (const el of root.querySelectorAll('[data-ink]')) {
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) continue;
    const dx = Math.max(r.left - x, 0, x - r.right);
    const dy = Math.max(r.top - y, 0, y - r.bottom);
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = el; }
  }
  return best;
}

// A point in the block's own unscaled pixels. The meeting scales the page to
// fit its column, and the ratio of the drawn width to the laid-out width says
// by how much.
function localPoint(el, e) {
  const r = el.getBoundingClientRect();
  const z = el.offsetWidth ? r.width / el.offsetWidth : 1;
  return [round((e.clientX - r.left) / (z || 1)), round((e.clientY - r.top) / (z || 1))];
}

/**
 * The marker over the blocks inside `rootRef`, while `enabled`. Returns the
 * stroke being drawn; `onCommit(stroke)` receives each finished one.
 */
export function useAnchoredInk({ rootRef, enabled, color, onCommit }) {
  const [live, setLive] = useState(null);
  const liveRef = useRef(null);
  const anchorRef = useRef(null);
  const activeRef = useRef(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) {
      liveRef.current = null;
      anchorRef.current = null;
      activeRef.current = null;
      setLive(null);
      return undefined;
    }

    const down = e => {
      if (e.target.closest && e.target.closest(CHROME)) return;
      if (e.button !== undefined && e.button !== 0) return;
      if (claimPointer(activeRef.current, e) === 'ignore') return;
      const el = anchorFor(root, e.target, e.clientX, e.clientY);
      if (!el) return;
      e.preventDefault();
      activeRef.current = { id: e.pointerId, type: e.pointerType };
      anchorRef.current = el;
      liveRef.current = { a: el.getAttribute('data-ink'), c: color || DEFAULT_INK, w: STROKE_WIDTH, p: [localPoint(el, e)] };
      setLive(liveRef.current);
      if (root.setPointerCapture && e.pointerId !== undefined) {
        try { root.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
      }
    };

    const move = e => {
      const stroke = liveRef.current;
      const el = anchorRef.current;
      if (!stroke || !el) return;
      if (activeRef.current && e.pointerId !== activeRef.current.id) return;
      const p = [...stroke.p];
      for (const sample of pointerSamples(e)) {
        const pt = localPoint(el, sample);
        const last = p[p.length - 1];
        if (last && Math.abs(last[0] - pt[0]) < MIN_STEP && Math.abs(last[1] - pt[1]) < MIN_STEP) continue;
        p.push(pt);
      }
      if (p.length !== stroke.p.length) {
        liveRef.current = { ...stroke, p };
        setLive(liveRef.current);
      }
    };

    const up = e => {
      const stroke = liveRef.current;
      if (!stroke) return;
      if (activeRef.current && e && e.pointerId !== activeRef.current.id) return;
      liveRef.current = null;
      anchorRef.current = null;
      activeRef.current = null;
      setLive(null);
      if (stroke.p.length) commitRef.current?.(stroke);
    };

    root.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      root.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [rootRef, enabled, color]);

  return live;
}
